import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  limit
} from 'firebase/firestore';
import { db } from './config';
import { logActivity } from './activityLogService';

const LEDGER_COLLECTION = 'cashBankLedger';
const ACCOUNTS_COLLECTION = 'cashBankAccounts';
const RECONCILIATION_COLLECTION = 'cashReconciliation';

/**
 * Initialize cash/bank accounts with opening balances
 */
export const initializeCashBankAccounts = async (openingBalances, currentUser) => {
  try {
    const batch = writeBatch(db);
    
    const accounts = [
      {
        id: 'cash_in_hand',
        accountName: 'Cash in Hand',
        accountType: 'cash_in_hand',
        currentBalance: openingBalances.cashInHand || 0,
        openingBalance: openingBalances.cashInHand || 0,
        totalCredits: 0,
        totalDebits: 0,
        lastUpdated: Timestamp.now(),
        lastReconciled: null,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.uid || 'system'
      },
      {
        id: 'petty_cash',
        accountName: 'Petty Cash',
        accountType: 'petty_cash',
        currentBalance: openingBalances.pettyCash || 0,
        openingBalance: openingBalances.pettyCash || 0,
        totalCredits: 0,
        totalDebits: 0,
        lastUpdated: Timestamp.now(),
        lastReconciled: null,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.uid || 'system'
      },
      {
        id: 'cash_at_bank',
        accountName: 'Cash at Bank',
        accountType: 'cash_at_bank',
        currentBalance: openingBalances.cashAtBank || 0,
        openingBalance: openingBalances.cashAtBank || 0,
        totalCredits: 0,
        totalDebits: 0,
        lastUpdated: Timestamp.now(),
        lastReconciled: null,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.uid || 'system'
      }
    ];

    for (const account of accounts) {
      const accountRef = doc(db, ACCOUNTS_COLLECTION, account.id);
      batch.set(accountRef, account);
    }

    await batch.commit();

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: 'initialized_accounts',
      entityType: 'cash_bank',
      entityId: 'accounts',
      entityName: 'Cash & Bank Accounts',
      details: { cashInHand: openingBalances.cashInHand || 0, pettyCash: openingBalances.pettyCash || 0, cashAtBank: openingBalances.cashAtBank || 0 }
    });

    return { success: true };
  } catch (error) {
    console.error('Error initializing accounts:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all accounts summary
 */
export const getCashBankAccounts = async () => {
  try {
    const accountsSnapshot = await getDocs(collection(db, ACCOUNTS_COLLECTION));
    
    const accounts = accountsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: accounts };
  } catch (error) {
    console.error('Error getting accounts:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get account balance
 */
export const getAccountBalance = async (accountType) => {
  try {
    const accountDoc = await getDoc(doc(db, ACCOUNTS_COLLECTION, accountType));
    
    if (!accountDoc.exists()) {
      return { success: false, error: 'Account not found', balance: 0 };
    }

    const accountData = accountDoc.data();
    return { 
      success: true, 
      balance: accountData.currentBalance || 0,
      data: accountData
    };
  } catch (error) {
    console.error('Error getting account balance:', error);
    return { success: false, error: error.message, balance: 0 };
  }
};

/**
 * Credit cash/bank account (money coming in)
 */
export const creditCashAccount = async (
  accountType, 
  amount, 
  referenceId, 
  referenceType = 'manual',
  description = '',
  paymentMethod = 'cash',
  chequeNumber = null,
  currentUser = null
) => {
  try {
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      return { success: false, error: 'Invalid amount' };
    }

    // Get current account balance
    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountType);
    const accountDoc = await getDoc(accountRef);
    
    if (!accountDoc.exists()) {
      return { success: false, error: 'Account not found' };
    }

    const accountData = accountDoc.data();
    const newBalance = (accountData.currentBalance || 0) + parsedAmount;
    const newTotalCredits = (accountData.totalCredits || 0) + parsedAmount;

    // Create ledger entry
    const ledgerEntry = {
      accountType,
      transactionType: 'credit',
      amount: parsedAmount,
      balance: newBalance,
      date: Timestamp.now(),
      reference: referenceId,
      referenceType,
      description,
      paymentMethod,
      chequeNumber,
      notes: '',
      createdBy: currentUser?.uid || 'system',
      createdAt: Timestamp.now()
    };

    await addDoc(collection(db, LEDGER_COLLECTION), ledgerEntry);

    // Update account balance
    await updateDoc(accountRef, {
      currentBalance: newBalance,
      totalCredits: newTotalCredits,
      lastUpdated: Timestamp.now()
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: 'cash_credit',
      entityType: 'cash_bank',
      entityId: referenceId,
      entityName: description || `Credit - ${accountType}`,
      details: { accountType, amount: parsedAmount, referenceType, newBalance }
    });

    return { success: true, newBalance };
  } catch (error) {
    console.error('Error crediting account:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Debit cash/bank account (money going out)
 */
export const debitCashAccount = async (
  accountType, 
  amount, 
  referenceId, 
  referenceType = 'manual',
  description = '',
  paymentMethod = 'cash',
  chequeNumber = null,
  currentUser = null
) => {
  try {
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      return { success: false, error: 'Invalid amount' };
    }

    // Get current account balance
    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountType);
    const accountDoc = await getDoc(accountRef);
    
    if (!accountDoc.exists()) {
      return { success: false, error: 'Account not found' };
    }

    const accountData = accountDoc.data();
    const currentBalance = accountData.currentBalance || 0;

    // Allow overdraft — balance can go negative (warn at UI level, not here)
    const newBalance = currentBalance - parsedAmount;
    const newTotalDebits = (accountData.totalDebits || 0) + parsedAmount;

    // Create ledger entry
    const ledgerEntry = {
      accountType,
      transactionType: 'debit',
      amount: parsedAmount,
      balance: newBalance,
      date: Timestamp.now(),
      reference: referenceId,
      referenceType,
      description,
      paymentMethod,
      chequeNumber,
      notes: '',
      createdBy: currentUser?.uid || 'system',
      createdAt: Timestamp.now()
    };

    await addDoc(collection(db, LEDGER_COLLECTION), ledgerEntry);

    // Update account balance
    await updateDoc(accountRef, {
      currentBalance: newBalance,
      totalDebits: newTotalDebits,
      lastUpdated: Timestamp.now()
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: 'cash_debit',
      entityType: 'cash_bank',
      entityId: referenceId,
      entityName: description || `Debit - ${accountType}`,
      details: { accountType, amount: parsedAmount, referenceType, newBalance }
    });

    return { success: true, newBalance };
  } catch (error) {
    console.error('Error debiting account:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Transfer funds between accounts
 */
export const transferFunds = async (fromAccount, toAccount, amount, notes = '', currentUser = null) => {
  try {
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      return { success: false, error: 'Invalid amount' };
    }

    if (fromAccount === toAccount) {
      return { success: false, error: 'Cannot transfer to the same account' };
    }

    // Debit from source account
    const debitResult = await debitCashAccount(
      fromAccount,
      parsedAmount,
      `transfer-${Date.now()}`,
      'transfer',
      `Transfer to ${toAccount}`,
      'transfer',
      null,
      currentUser
    );

    if (!debitResult.success) {
      return debitResult;
    }

    // Credit to destination account
    const creditResult = await creditCashAccount(
      toAccount,
      parsedAmount,
      `transfer-${Date.now()}`,
      'transfer',
      `Transfer from ${fromAccount}`,
      'transfer',
      null,
      currentUser
    );

    if (!creditResult.success) {
      // Rollback debit if credit fails
      await creditCashAccount(
        fromAccount,
        parsedAmount,
        `rollback-${Date.now()}`,
        'adjustment',
        'Rollback failed transfer',
        'adjustment',
        null,
        currentUser
      );
      return { success: false, error: 'Transfer failed, transaction rolled back' };
    }

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: 'cash_transfer',
      entityType: 'cash_bank',
      entityId: `transfer-${Date.now()}`,
      entityName: `Transfer: ${fromAccount} → ${toAccount}`,
      details: { fromAccount, toAccount, amount: parsedAmount }
    });

    return { success: true, message: 'Transfer completed successfully' };
  } catch (error) {
    console.error('Error transferring funds:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get transactions for an account
 */
export const getTransactions = async (filters = {}) => {
  try {
    let q = collection(db, LEDGER_COLLECTION);
    
    const constraints = [];
    
    if (filters.accountType) {
      constraints.push(where('accountType', '==', filters.accountType));
    }
    
    if (filters.transactionType) {
      constraints.push(where('transactionType', '==', filters.transactionType));
    }
    
    if (filters.referenceType) {
      constraints.push(where('referenceType', '==', filters.referenceType));
    }
    
    constraints.push(orderBy('createdAt', 'desc'));
    
    if (filters.limit) {
      constraints.push(limit(filters.limit));
    }
    
    if (constraints.length > 0) {
      q = query(q, ...constraints);
    }
    
    const snapshot = await getDocs(q);
    const transactions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: transactions };
  } catch (error) {
    console.error('Error getting transactions:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Add manual transaction (adjustment, deposit, withdrawal)
 */
export const addManualTransaction = async (transactionData, currentUser) => {
  try {
    const { accountType, transactionType, amount, description, paymentMethod, reference } = transactionData;

    if (transactionType === 'credit') {
      return await creditCashAccount(
        accountType,
        amount,
        reference || `manual-${Date.now()}`,
        'manual',
        description,
        paymentMethod,
        null,
        currentUser
      );
    } else if (transactionType === 'debit') {
      return await debitCashAccount(
        accountType,
        amount,
        reference || `manual-${Date.now()}`,
        'manual',
        description,
        paymentMethod,
        null,
        currentUser
      );
    } else {
      return { success: false, error: 'Invalid transaction type' };
    }
  } catch (error) {
    console.error('Error adding manual transaction:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reconcile account
 */
export const reconcileAccount = async (reconciliationData, currentUser) => {
  try {
    const { accountType, date, systemBalance, physicalCount, difference, notes } = reconciliationData;

    const reconciliationEntry = {
      accountType,
      date: date ? Timestamp.fromDate(new Date(date)) : Timestamp.now(),
      systemBalance: parseFloat(systemBalance),
      physicalCount: parseFloat(physicalCount),
      difference: parseFloat(difference),
      notes: notes || '',
      reconciledBy: currentUser?.uid || 'system',
      reconciledAt: Timestamp.now()
    };

    await addDoc(collection(db, RECONCILIATION_COLLECTION), reconciliationEntry);

    // Update account's last reconciled date
    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountType);
    await updateDoc(accountRef, {
      lastReconciled: Timestamp.now()
    });

    // If there's a difference, create adjustment entry
    if (Math.abs(difference) > 0.01) {
      const adjustmentType = difference > 0 ? 'credit' : 'debit';
      const adjustmentAmount = Math.abs(difference);
      
      if (adjustmentType === 'credit') {
        await creditCashAccount(
          accountType,
          adjustmentAmount,
          `reconciliation-${Date.now()}`,
          'adjustment',
          `Reconciliation adjustment: ${notes}`,
          'adjustment',
          null,
          currentUser
        );
      } else {
        await debitCashAccount(
          accountType,
          adjustmentAmount,
          `reconciliation-${Date.now()}`,
          'adjustment',
          `Reconciliation adjustment: ${notes}`,
          'adjustment',
          null,
          currentUser
        );
      }
    }

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: 'cash_reconcile',
      entityType: 'cash_bank',
      entityId: accountType,
      entityName: `Reconciliation - ${accountType}`,
      details: { accountType, systemBalance, physicalCount, difference }
    });

    return { success: true };
  } catch (error) {
    console.error('Error reconciling account:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get reconciliation history
 */
export const getReconciliationHistory = async (accountType = null) => {
  try {
    let q = collection(db, RECONCILIATION_COLLECTION);
    
    if (accountType) {
      q = query(q, where('accountType', '==', accountType), orderBy('reconciledAt', 'desc'));
    } else {
      q = query(q, orderBy('reconciledAt', 'desc'));
    }
    
    const snapshot = await getDocs(q);
    const history = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: history };
  } catch (error) {
    console.error('Error getting reconciliation history:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get cash flow summary
 */
export const getCashFlowSummary = async (startDate = null, endDate = null) => {
  try {
    let q = collection(db, LEDGER_COLLECTION);
    
    const constraints = [];
    
    if (startDate) {
      constraints.push(where('date', '>=', Timestamp.fromDate(new Date(startDate))));
    }
    
    if (endDate) {
      constraints.push(where('date', '<=', Timestamp.fromDate(new Date(endDate))));
    }
    
    constraints.push(orderBy('date', 'desc'));
    
    if (constraints.length > 0) {
      q = query(q, ...constraints);
    }
    
    const snapshot = await getDocs(q);
    
    let totalInflow = 0;
    let totalOutflow = 0;
    const inflowByType = {};
    const outflowByType = {};
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const amount = data.amount || 0;
      const type = data.referenceType || 'other';
      
      if (data.transactionType === 'credit') {
        totalInflow += amount;
        inflowByType[type] = (inflowByType[type] || 0) + amount;
      } else if (data.transactionType === 'debit') {
        totalOutflow += amount;
        outflowByType[type] = (outflowByType[type] || 0) + amount;
      }
    });

    return {
      success: true,
      data: {
        totalInflow,
        totalOutflow,
        netCashFlow: totalInflow - totalOutflow,
        inflowByType,
        outflowByType
      }
    };
  } catch (error) {
    console.error('Error getting cash flow summary:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete transaction (admin only, with restrictions)
 */
export const deleteTransaction = async (transactionId, currentUser) => {
  try {
    const transactionRef = doc(db, LEDGER_COLLECTION, transactionId);
    const transactionDoc = await getDoc(transactionRef);
    
    if (!transactionDoc.exists()) {
      return { success: false, error: 'Transaction not found' };
    }

    const transactionData = transactionDoc.data();
    
    // Only allow deletion of manual transactions
    if (transactionData.referenceType !== 'manual' && transactionData.referenceType !== 'adjustment') {
      return { 
        success: false, 
        error: 'Cannot delete system-generated transactions. Please delete the source record instead.' 
      };
    }

    // Reverse the transaction
    const accountRef = doc(db, ACCOUNTS_COLLECTION, transactionData.accountType);
    const accountDoc = await getDoc(accountRef);
    
    if (accountDoc.exists()) {
      const accountData = accountDoc.data();
      let newBalance = accountData.currentBalance || 0;
      let newTotalCredits = accountData.totalCredits || 0;
      let newTotalDebits = accountData.totalDebits || 0;
      
      if (transactionData.transactionType === 'credit') {
        newBalance -= transactionData.amount;
        newTotalCredits -= transactionData.amount;
      } else {
        newBalance += transactionData.amount;
        newTotalDebits -= transactionData.amount;
      }
      
      await updateDoc(accountRef, {
        currentBalance: newBalance,
        totalCredits: newTotalCredits,
        totalDebits: newTotalDebits,
        lastUpdated: Timestamp.now()
      });
    }

    await deleteDoc(transactionRef);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: 'deleted_transaction',
      entityType: 'cash_bank',
      entityId: transactionId,
      entityName: transactionData.description || `Transaction - ${transactionData.accountType}`,
      details: { accountType: transactionData.accountType, amount: transactionData.amount }
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting transaction:', error);
    return { success: false, error: error.message };
  }
};
